import { ExpenseCategory, ExpenseStatus } from './expense';

/**
 * Report type enumeration
 * Requirements: 7.1, 7.2, 7.3 - Different report types
 */
export enum ReportType {
  MONTHLY = 'Monthly',
  DEPARTMENT = 'Department',
  EMPLOYEE = 'Employee',
  CATEGORY = 'Category'
}

/**
 * Report filter interface for multi-criteria filtering
 * Requirements: 4.5, 7.1, 7.2, 7.3 - Report filtering
 */
export interface ReportFilters {
  dateFrom?: string;
  dateTo?: string;
  department?: string;
  employeeId?: string;
  categories?: ExpenseCategory[];
  statuses?: ExpenseStatus[];
  minAmount?: number;
  maxAmount?: number;
}

/**
 * Report data interface for generated reports
 * Requirements: 7.1, 7.2, 7.3 - Report data structure
 */
export interface ReportData {
  id: string;
  type: ReportType;
  title: string;
  filters: ReportFilters;
  generatedAt: string;
  totalExpenses: number;
  totalAmount: number;
  summary: ReportSummary;
  details: ReportDetail[];
}

/**
 * Report summary interface for aggregated data
 * Requirements: 7.1, 7.2, 7.3 - Report summaries
 */
export interface ReportSummary {
  totalExpenses: number;
  totalAmount: number;
  averageAmount: number;
  categoryBreakdown: CategoryBreakdown[];
  statusBreakdown: StatusBreakdown[];
  monthlyBreakdown?: MonthlyBreakdown[];
  departmentBreakdown?: DepartmentBreakdown[];
}

/**
 * Category breakdown for spending analysis
 * Requirements: 7.1, 7.2, 7.3 - Category-wise analysis
 */
export interface CategoryBreakdown {
  category: ExpenseCategory;
  count: number;
  amount: number;
  percentage: number;
}

/**
 * Status breakdown for workflow analysis
 */
export interface StatusBreakdown {
  status: ExpenseStatus;
  count: number;
  amount: number;
  percentage: number;
}

/**
 * Monthly breakdown for trend analysis
 * Requirements: 7.1 - Monthly reports
 */
export interface MonthlyBreakdown {
  month: string; // YYYY-MM format
  count: number;
  amount: number;
}

/**
 * Department breakdown for organizational analysis
 * Requirements: 7.2 - Department reports
 */
export interface DepartmentBreakdown {
  department: string;
  count: number;
  amount: number;
  employeeCount: number;
}

/**
 * Report detail interface for individual expense data
 * Requirements: 7.3 - Employee reports with details
 */
export interface ReportDetail {
  expenseId: string;
  title: string;
  category: ExpenseCategory;
  amount: number;
  date: string;
  status: ExpenseStatus;
  submitterName: string;
  submitterDepartment: string;
  approvalDate?: string;
  reimbursementDate?: string;
}

/**
 * Report generation input interface
 * Requirements: 4.5, 7.1, 7.2, 7.3 - Report generation parameters
 */
export interface GenerateReportInput {
  type: ReportType;
  filters: ReportFilters;
  includeDetails?: boolean;
}

/**
 * Report export format enumeration
 * Requirements: 7.4 - Export formats
 */
export enum ExportFormat {
  CSV = 'CSV',
  PDF = 'PDF'
}

/**
 * Report export input interface
 * Requirements: 7.4 - Report export
 */
export interface ExportReportInput {
  reportId: string;
  format: ExportFormat;
  includeDetails?: boolean;
}

/**
 * Export result interface
 * Requirements: 7.4 - Export result tracking
 */
export interface ExportResult {
  id: string;
  reportId: string;
  format: ExportFormat;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  exportedAt: string;
  expiresAt: string;
}